import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import type { KindIconRow, KindIconsAdminResponse } from '../api/types';

/**
 * Ícones da barra de categorias ("Todas", "Rifles", "Snipers"…).
 *
 * O site escolhe sozinho a arte de cada categoria a partir do catálogo, e a
 * escolha automática acerta na maioria — mas a cara da loja é decisão da loja.
 * Aqui cada categoria pode receber a imagem que o admin quiser, e voltar ao
 * automático com um clique.
 *
 * Três caminhos, do mais curto ao mais trabalhoso:
 *   • clicar numa skin do próprio catálogo daquela categoria;
 *   • colar o endereço de uma imagem;
 *   • subir um arquivo, que viaja embutido (data:) — assim não é preciso ter
 *     onde hospedar a imagem.
 */

/** Arquivo maior que isto vira um JSON de configuração pesado à toa. */
const MAX_ARQUIVO_BYTES = 200 * 1024;

export function KindIconsPanel() {
  const [data, setData] = useState<KindIconsAdminResponse | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  const [endereco, setEndereco] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async (kind?: string) => {
    try {
      setData(await api.adminKindIcons(kind));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os ícones.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function abrir(row: KindIconRow) {
    setOk(null);
    setErro(null);
    setEndereco(row.manual ? row.url ?? '' : '');
    setEditando(row.kind);
    if (inputRef.current) inputRef.current.value = '';
    void carregar(row.kind);
  }

  async function salvar(kind: string, url: string | null, aviso: string) {
    setOcupado(true);
    setErro(null);
    try {
      setData(await api.adminSetKindIcon(kind, url));
      setOk(aviso);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar o ícone.');
    } finally {
      setOcupado(false);
    }
  }

  async function subirArquivo(kind: string, file: File) {
    if (file.size > MAX_ARQUIVO_BYTES) {
      setErro(`"${file.name}" tem ${Math.round(file.size / 1024)} KB. O limite para ícone é 200 KB.`);
      return;
    }
    setOcupado(true);
    setErro(null);
    try {
      const dataUrl = await lerComoDataUrl(file);
      setData(await api.adminSetKindIcon(kind, dataUrl));
      setOk(`Ícone de "${kind}" trocado pelo arquivo enviado.`);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui ler o arquivo.');
    } finally {
      setOcupado(false);
    }
  }

  if (!data) return erro ? <Banner kind="error">{erro}</Banner> : <Spinner label="Carregando os ícones…" />;

  const aberta = data.items.find((i) => i.kind === editando) ?? null;

  return (
    <div className="panel">
      <h2>Ícones das categorias</h2>
      <p className="muted">
        A imagem de cada botão da barra de categorias do mercado. Sem escolha aqui, o site usa a arte da skin mais
        vendida do tipo. A troca vale para todo mundo assim que a página do site é recarregada.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <div className="kind-icon-grid">
        {data.items.map((row) => (
          <button
            key={row.kind}
            className={`kind-icon-card${editando === row.kind ? ' active' : ''}`}
            onClick={() => abrir(row)}
            title={`Trocar o ícone de ${row.pt}`}
          >
            {row.url ? <img src={row.url} alt="" /> : <span className="dim">sem ícone</span>}
            <span className="kind-icon-name">{row.pt}</span>
            <span className={`kind-icon-tag ${row.manual ? 'manual' : ''}`}>{row.manual ? 'escolhido' : 'automático'}</span>
          </button>
        ))}
      </div>

      {aberta && (
        <div className="panel kind-icon-editor">
          <h3>
            {aberta.pt} <span className="dim">({aberta.kind})</span>
          </h3>

          <div className="kind-icon-current">
            <figure>
              {aberta.url ? <img src={aberta.url} alt="" /> : <span className="dim">—</span>}
              <figcaption className="dim">valendo hoje</figcaption>
            </figure>
            {aberta.manual && aberta.autoUrl && (
              <figure>
                <img src={aberta.autoUrl} alt="" />
                <figcaption className="dim">automático</figcaption>
              </figure>
            )}
            {aberta.manual && (
              <button
                className="btn ghost"
                disabled={ocupado}
                onClick={() => void salvar(aberta.kind, null, `"${aberta.pt}" voltou ao ícone automático.`)}
              >
                Voltar ao automático
              </button>
            )}
          </div>

          <h4>Escolher uma skin do catálogo</h4>
          {data.sugestoes.length === 0 ? (
            <p className="muted">
              Nenhuma arte deste tipo no catálogo ainda. Rode uma varredura na aba Catálogo, ou use um endereço.
            </p>
          ) : (
            <div className="kind-icon-suggestions">
              {data.sugestoes.map((s) => (
                <button
                  key={s.iconUrl}
                  className="kind-icon-suggestion"
                  title={s.name}
                  disabled={ocupado}
                  onClick={() => void salvar(aberta.kind, s.iconUrl, `"${aberta.pt}" agora usa a arte de ${s.name}.`)}
                >
                  <img src={s.iconUrl} alt="" />
                </button>
              ))}
            </div>
          )}

          <h4>Endereço de uma imagem</h4>
          <div className="actions-row">
            <input
              className="input"
              placeholder="https://…/imagem.png"
              value={endereco}
              onChange={(e) => setEndereco(e.target.value)}
            />
            <button
              className="btn primary"
              disabled={ocupado || !endereco.trim()}
              onClick={() => void salvar(aberta.kind, endereco.trim(), `"${aberta.pt}" agora usa o endereço informado.`)}
            >
              Usar este endereço
            </button>
          </div>

          <h4>Ou subir um arquivo</h4>
          <p className="muted">
            PNG, JPG, WEBP ou SVG de até 200 KB. O arquivo é guardado junto da configuração, então não é preciso
            hospedá-lo em lugar nenhum.
          </p>
          <input
            ref={inputRef}
            className="file-input"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            disabled={ocupado}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void subirArquivo(aberta.kind, file);
            }}
          />
        </div>
      )}
    </div>
  );
}

function lerComoDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}
